// Portfolio State Management with Zustand

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import type { Portfolio, Position } from '../types/api';

interface PortfolioState {
  portfolio: Portfolio | null;
  positions: Position[];
  isLoading: boolean;
  error: string | null;
  lastUpdated: string | null;
  
  // Actions
  setPortfolio: (portfolio: Portfolio) => void;
  setPositions: (positions: Position[]) => void;
  updatePosition: (symbol: string, updates: Partial<Position>) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setLastUpdated: (timestamp: string) => void;
  
  // Computed values (as functions)
  getTotalValue: () => number;
  getTotalPnL: () => number;
  getPositionCount: () => number;
  getLongPositions: () => Position[];
  getShortPositions: () => Position[];
}

export const usePortfolioStore = create<PortfolioState>()(
  subscribeWithSelector((set, get) => ({
    portfolio: null,
    positions: [],
    isLoading: false,
    error: null,
    lastUpdated: null,

    setPortfolio: (portfolio) => set({ portfolio, lastUpdated: new Date().toISOString() }),
    
    setPositions: (positions) => set({ positions, lastUpdated: new Date().toISOString() }),
    
    updatePosition: (symbol, updates) =>
      set((state) => ({
        positions: state.positions.map((pos) =>
          pos.symbol === symbol ? { ...pos, ...updates } : pos
        ),
        lastUpdated: new Date().toISOString(),
      })),
    
    setLoading: (isLoading) => set({ isLoading }),
    
    setError: (error) => set({ error }),
    
    setLastUpdated: (lastUpdated) => set({ lastUpdated }),

    getTotalValue: () => {
      const { portfolio } = get();
      return portfolio?.totalValue ?? 0;
    },

    getTotalPnL: () => {
      const { portfolio } = get();
      return portfolio?.totalPnL ?? 0;
    },

    getPositionCount: () => {
      const { positions } = get();
      return positions.length;
    },

    getLongPositions: () => {
      const { positions } = get();
      return positions.filter((pos) => pos.side === 'long');
    },

    getShortPositions: () => {
      const { positions } = get();
      return positions.filter((pos) => pos.side === 'short');
    },
  }))
);

// Selector hooks for optimized re-renders
export const usePortfolio = () => usePortfolioStore((state) => state.portfolio);
export const usePositions = () => usePortfolioStore((state) => state.positions);
export const usePortfolioLoading = () => usePortfolioStore((state) => state.isLoading);
export const usePortfolioError = () => usePortfolioStore((state) => state.error);

// Derived position hooks for convenience
export const usePositionList = () => usePortfolioStore((state) => state.positions);
export const useActivePositions = () => 
  usePortfolioStore((state) => state.positions.filter((pos) => pos.size > 0));
